// Session mode (on_site_sessions/show): the page a photographer keeps open at
// the restaurant.
//
// Plates tick instantly and save in the background. Restaurant back rooms
// have bad signal, so a save that fails is queued in localStorage and replayed
// when the browser comes back online or the page is reopened; the tick never
// waits on the network and never gets lost.
//
// Check-in grabs the phone's position (best effort, a few seconds at most)
// before submitting, so ops can see the photographer was at the venue.
import { Controller } from "@hotwired/stimulus"

const LOCATE_TIMEOUT_MS = 6000

export default class extends Controller {
  static targets = [
    "plate", "list", "bar", "shotCount", "pendingCount", "sync",
    "tallyShot", "tallyUnavailable", "tallyPending", "tallyPendingBox",
    "finishLabel", "countInput",
    "checkinForm", "checkinButton", "lat", "lng",
    "selfieInput", "selfiePreview", "selfieDrop",
    "addDialog", "addName", "dock", "wrapUp", "guideDialog"
  ]
  static values = {
    shooting: Number,
    finishLabel: String, finishMissing: String,
    saving: String, saved: String, offline: String, locating: String
  }

  connect() {
    this.onOnline = () => this.flushQueue()
    window.addEventListener("online", this.onOnline)
    this.applyQueuedStates()
    this.flushQueue()
    this.watchWrapUp()
  }

  disconnect() {
    window.removeEventListener("online", this.onOnline)
    this.wrapUpObserver?.disconnect()
  }

  // --- Bottom dock ----------------------------------------------------
  // The dock carries "go to wrap-up"; once the wrap-up card (and its own
  // finish button) is on screen, the dock steps aside.
  watchWrapUp() {
    if (!this.hasDockTarget || !this.hasWrapUpTarget || !("IntersectionObserver" in window)) return
    this.wrapUpObserver = new IntersectionObserver(([entry]) => {
      this.dockTarget.classList.toggle("is-hidden", entry.isIntersecting)
    }, { threshold: 0.15 })
    this.wrapUpObserver.observe(this.wrapUpTarget)
  }

  goToWrapUp() {
    this.wrapUpTarget.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  // --- Plates ---------------------------------------------------------

  toggleShot(event) {
    const plate = event.currentTarget.closest("[data-state]")
    this.setState(plate, plate.dataset.state === "shot" ? "pending" : "shot")
  }

  toggleUnavailable(event) {
    const plate = event.currentTarget.closest("[data-state]")
    this.setState(plate, plate.dataset.state === "unavailable" ? "pending" : "unavailable")
  }

  setState(plate, state) {
    plate.dataset.state = state
    if (navigator.vibrate && state === "shot") navigator.vibrate(12)
    this.recount()
    this.enqueue(plate.dataset.url, state)
    this.flushQueue()
  }

  filter(event) {
    const button = event.currentTarget
    this.listTarget.dataset.filter = button.dataset.filter
    button.parentElement.querySelectorAll("button").forEach((b) => b.classList.toggle("is-active", b === button))
  }

  recount() {
    const states = this.plateTargets.map((p) => p.dataset.state)
    const total = states.length
    const shot = states.filter((s) => s === "shot").length
    const unavailable = states.filter((s) => s === "unavailable").length
    const pending = total - shot - unavailable

    this.shotCountTargets.forEach((el) => (el.textContent = shot))
    this.pendingCountTargets.forEach((el) => (el.textContent = pending))
    if (this.hasBarTarget) this.barTarget.style.width = `${total ? Math.round((100 * (shot + unavailable)) / total) : 0}%`
    if (this.hasTallyShotTarget) this.tallyShotTarget.textContent = shot
    if (this.hasTallyUnavailableTarget) this.tallyUnavailableTarget.textContent = unavailable
    if (this.hasTallyPendingTarget) this.tallyPendingTarget.textContent = pending
    if (this.hasTallyPendingBoxTarget) this.tallyPendingBoxTarget.classList.toggle("is-warn", pending > 0)
    if (this.hasFinishLabelTarget) {
      this.finishLabelTarget.textContent = pending > 0
        ? this.finishMissingValue.replace("__COUNT__", pending)
        : this.finishLabelValue
    }
  }

  // --- Save queue (one pending state per plate, last tap wins) ----------

  get queueKey() { return `on-site:${this.shootingValue}` }

  readQueue() {
    try { return JSON.parse(localStorage.getItem(this.queueKey) || "{}") } catch (_) { return {} }
  }

  writeQueue(queue) {
    try {
      if (Object.keys(queue).length) localStorage.setItem(this.queueKey, JSON.stringify(queue))
      else localStorage.removeItem(this.queueKey)
    } catch (_) { /* storage blocked: saves still go straight to the server */ }
  }

  enqueue(url, state) {
    const queue = this.readQueue()
    queue[url] = state
    this.writeQueue(queue)
    this.pendingMemory = { ...(this.pendingMemory || {}), [url]: state }
  }

  // A reopened page renders server state; re-apply taps that never made it.
  applyQueuedStates() {
    const queue = this.readQueue()
    if (!Object.keys(queue).length) return
    this.plateTargets.forEach((plate) => {
      if (queue[plate.dataset.url]) plate.dataset.state = queue[plate.dataset.url]
    })
    this.recount()
  }

  async flushQueue() {
    if (this.flushing) { this.flushAgain = true; return }
    const queue = { ...this.readQueue(), ...(this.pendingMemory || {}) }
    const urls = Object.keys(queue)
    if (!urls.length) return

    this.flushing = true
    this.showSync("saving")
    let failed = false
    for (const url of urls) {
      const state = queue[url]
      try {
        const res = await fetch(url, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this.csrf },
          body: JSON.stringify({ state }),
          credentials: "same-origin"
        })
        if (!res.ok) throw new Error(res.status)
        // Only clear it if no newer tap replaced it while in flight.
        const latest = this.readQueue()
        if (latest[url] === state) { delete latest[url]; this.writeQueue(latest) }
        if (this.pendingMemory?.[url] === state) delete this.pendingMemory[url]
      } catch (_) {
        failed = true
        break
      }
    }
    this.flushing = false
    this.showSync(failed ? "offline" : "saved")
    if (failed) {
      clearTimeout(this.retryTimer)
      this.retryTimer = setTimeout(() => this.flushQueue(), 15000)
    } else if (this.flushAgain) {
      this.flushAgain = false
      this.flushQueue()
    }
  }

  showSync(kind) {
    const text = { saving: this.savingValue, saved: this.savedValue, offline: this.offlineValue }[kind]
    this.syncTargets.forEach((el) => { el.dataset.kind = kind; el.textContent = text })
    clearTimeout(this.syncTimer)
    if (kind === "saved") this.syncTimer = setTimeout(() => this.syncTargets.forEach((el) => { el.textContent = "" }), 2000)
  }

  get csrf() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  // --- Check-in -------------------------------------------------------

  selfieChanged() {
    const file = this.selfieInputTarget.files[0]
    if (file) {
      const url = URL.createObjectURL(file)
      this.selfiePreviewTarget.src = url
      this.selfiePreviewTarget.hidden = false
      this.selfieDropTarget.classList.add("has-file")
    }
    this.checkinButtonTargets.forEach((button) => {
      if (button.dataset.selfieRequired === "true") button.disabled = !file
    })
  }

  checkIn(event) {
    if (this.located) return // second pass: let the form go
    event.preventDefault()
    this.checkinButtonTargets.forEach((button) => {
      button.disabled = true
      button.querySelector("span").textContent = this.locatingValue
    })

    const submit = () => {
      this.located = true
      this.checkinFormTarget.requestSubmit ? this.checkinFormTarget.requestSubmit() : this.checkinFormTarget.submit()
    }
    if (!navigator.geolocation) return submit()

    const fallback = setTimeout(submit, LOCATE_TIMEOUT_MS + 500)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(fallback)
        this.latTarget.value = pos.coords.latitude
        this.lngTarget.value = pos.coords.longitude
        submit()
      },
      () => { clearTimeout(fallback); submit() },
      { enableHighAccuracy: true, timeout: LOCATE_TIMEOUT_MS, maximumAge: 60000 }
    )
  }

  // --- Add a plate (restaurant asks for one not on the list) ---------

  openAddPlate() {
    this.addDialogTarget.showModal()
    this.addNameTarget.focus()
  }

  closeAddPlate() {
    this.addDialogTarget.close()
  }

  // A tap on the dimmed area outside a sheet closes it.
  dialogBackdrop(event) {
    if (event.target === event.currentTarget) event.currentTarget.close()
  }

  // --- Client guide ---------------------------------------------------
  // Opened from a step's tips, it lands on that step's section.

  openGuide(event) {
    const dialog = this.guideDialogTarget
    dialog.showModal()
    // Land on the title, not the first link, so nothing looks pre-selected.
    dialog.querySelector("h2")?.focus({ preventScroll: true })
    const group = event?.currentTarget?.dataset?.group
    const section = group && dialog.querySelector(`#guide-${group}`)
    if (section) section.scrollIntoView({ block: "start" })
    else dialog.scrollTop = 0
  }

  closeGuide() {
    this.guideDialogTarget.close()
  }

  // --- Wrap-up --------------------------------------------------------

  stepCount(event) {
    const input = this.countInputTarget
    const next = Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(event.currentTarget.dataset.step, 10))
    input.value = next || ""
  }

  // --- Satisfaction form ---------------------------------------------

  async copy(event) {
    const button = event.currentTarget
    try {
      await navigator.clipboard.writeText(button.dataset.url)
      const label = button.querySelector("span")
      const before = label.textContent
      label.textContent = button.dataset.done
      setTimeout(() => (label.textContent = before), 1800)
    } catch (_) {
      window.open(button.dataset.url, "_blank", "noopener")
    }
  }
}
